import React, {forwardRef, memo, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {useAuth0} from "@auth0/auth0-react";
import {withStyles} from "@material-ui/core/styles";
import __ from "lodash";

import DataGrid from "../../../controls/DataGrid";
import AirportFuelSupplierLookup from "../../Companies/components/AirportFuelSupplierLookup";
import CompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";

const styles = {
  header: {
    "&.ag-header-cell": {
      paddingLeft: "17px !important",
      paddingRight: "17px !important",
    }
  },
  column: {
    "&.ag-cell-not-inline-editing": {
      paddingLeft: "17px !important",
      paddingRight: "17px !important",
    },
    "&.ag-cell-focus,.ag-cell-no-focus" : {
      border: "none !important",
    },
    /* This CSS is to not apply the border for the column having 'no-border' class */
    "&.no-border.ag-cell:focus": {
      border: "none !important",
      outline: "none"
    }
  },
  numericHeader: {
    "&.ag-header-cell": {
      paddingRight: 1, fontSize: 12,
    },
    "&.ag-cell-label-container": {
      flexDirection: "row"
    },
    "&.ag-header-cell-label": {
      flexDirection: "rowReverse"
    },
  },
  numericColumn: {
    "&.ag-cell-not-inline-editing": {
      paddingLeft:4, paddingRight: 4, textAlign: "right"
    }
  },
};


const ServiceProvider =  forwardRef((props, ref) => {
  const params = props.params;
  const data = params.data;
  const [value, setValue] = useState(params.value);

  const refInput = useRef();

  useImperativeHandle(ref, () => {
    return {
      getValue: () => {
        return value;
      }
    }
  });

  return(
    <div>
      {data.serviceCode === "FUELING"
      ?
        <AirportFuelSupplierLookup
          ref={refInput}
          returnType="object"
          value={value}
          airportCode={props.airportCode}
          width={"450px"}
          type={props.type}
          onChange={(returnObject) => {
            if (returnObject.found) {
              const fuelSupplier = returnObject.data;
              if (!__.isEmpty(fuelSupplier)) {
                const data = {
                  serviceProviderUuid: fuelSupplier.companyUuid,
                  serviceProviderName: fuelSupplier.companyName,
                  isRequired: true
                }
                props.onChange(params, data);
                setValue(fuelSupplier.companyUuid);
              }
            } else {
              props.onChange(params, returnObject.value);
              setValue(returnObject.value);
            }
          }}
        />
      :
        <CompanyUuidLookup
          ref={refInput}
          companyType={data.companyType}
          value={value}
          returnType={"object"}
          atAirport={props.airportCode}
          role={data.companyRole}
          isDataGrid={true}
          type={props.type}
          //width={`calc(100% - ${(400)}px)`}
          width={"450px"}
          onChange={(returnObject) => {
            if (returnObject.found) {
              const contractedParty = returnObject.data
              const data = {
                serviceProviderName: contractedParty.companyName,
                serviceProviderUuid: contractedParty.companyUuid,
                isRequired: true
              }
              props.onChange(params, data);
              setValue(contractedParty.companyUuid);
            } else {
              props.onChange(params, returnObject.value);
              setValue(returnObject.value);
            }
          }}
        />
      }
    </div>
  );
});



const AirportServiceProvidersGrid = (_props) => {
  const {isAuthenticated} = useAuth0();
  const props = {
    ..._props
  }

  const { classes } = props;

  const dataGridRef = useRef();
  const providerRef = useRef();
  const [services, setServices] = useState(props.services);
  if ( !__.isEqual(services, props.services) )
    setServices(props.services);

  const onGridReady = (params) => {
    params.api.sizeColumnsToFit();
    if (props.onGridReady) {
      props.onGridReady(params);
    }
  };

  const updateParent = (params, servicesTypeUuid) => {
    let model;
    let visibleRows = [];
    if(params.api){
      model = params.api.getModel();
      visibleRows = model.rowsToDisplay;
    }

    const serviceProviders = [];
    visibleRows.forEach(row => {
      if (row.data.servicesTypeUuid === servicesTypeUuid) {
        serviceProviders.push(row.data);
      }
    });

    if (props.onChange) {
      props.onChange(serviceProviders);
    }
  }

  const getColumns = (props) => {
    const baseColumn = {
      options: {
        //cellStyle: { fontSize: 7, },
        cellClass: classes.column,
        headerClass: classes.header,
      },
      editable: props.canEdit,
      //headerClass: 'classes'
    };

    return [
      {
        ...baseColumn,
        headerName: "Service",
        field: "serviceName",
        width: 250,
        editable: false,
        suppressNavigable: true,
      },
      {
        ...baseColumn,
        headerName: "Direction",
        field: "direction",
        width: 150,
        editable: false,
        suppressNavigable: true,
        valueFormatter: (params) => {
          return (params.node.data.direction  === "OUT" ? "Outbound" : "Inbound");
        }
      },
      {
        headerName: "Service Provider",
        ...baseColumn,
        field: "companyUuid",
        flex: 1,
        cellEditorSelector: (params) => { return {
          component: "ServiceProvider",
          params: {
            ref: providerRef,
            airportCode: props.airportCode,
            params: params,
            onChange: (params, returnValue) => {
              if (returnValue && typeof returnValue === "object") {
                params.node.data.companyUuid = returnValue.serviceProviderUuid;
                updateParent(params, params.node.data.servicesTypeUuid);
                //setTimeout(function() { params.api.redrawRows({rowNodes: params.node}); }, 0);
              }
            }
          }
        }},
        cellRendererSelector: (params) => { return {
          component: "ServiceProvider",
          params: {
            ref: providerRef,
            airportCode: props.airportCode,
            params: params,
            type: "render",
          }
        }},
      },
    ];
  };

  return (isAuthenticated &&
    <div style={{width: props.width?props.width:"100%", ...props.style}}>
      <DataGrid
        title="Default Service Providers"
        ref={dataGridRef}
        columns={getColumns({canEdit: props.canEdit, airportCode: props.airportCode})}
        rows={services}
        height={props.height||`calc(100vh - ${(290)}px)`}
        components={{ServiceProvider}}
        options={{
          singleClickEdit: true,
          editType:'fullRow',
          //onCellValueChanged: ()=>{ }, //prevent default onCellValueChanged
          defaultColDef: {
            editable: false,
            resizable: false,
            menuTabs: []
          }
        }}
        onGridReady={onGridReady}
      />
    </div>
  );

}

export default withStyles(styles)(AirportServiceProvidersGrid);
